import { useCallback, useEffect, useState } from "react";
import { ScanLine, QrCode, Camera, Loader2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { useSession } from "@/components/SessionProvider";
import { useLocation } from "wouter";
import { redeemFriendUrl } from "@/lib/friendInvite";

// b20 (Sam 2026-07-02): real QR scanning for the Friends → Scan tab. Replaces
// the old decorative mockup (corner brackets + sweep beam, no camera). Uses the
// native ML Kit barcode scanner via @capacitor-mlkit/barcode-scanning, which
// runs a full-screen native camera and returns the decoded value. On a scanned
// nexusdex://add-friend?token=... URL we hand straight to the shared
// redeemFriendUrl() so a scan auto-creates the friend request — identical to the
// OS deep-link path. Web/non-native builds fall back to the "enter code" tabs.

type ScanState = "idle" | "checking" | "scanning" | "unsupported";

export function QrScanTab({ onShowQr }: { onShowQr: () => void }) {
  const { toast } = useToast();
  const { account } = useAuth();
  const { openPaywall } = useSession();
  const [, setLocation] = useLocation();
  const [state, setState] = useState<ScanState>("idle");
  const [supported, setSupported] = useState<boolean | null>(null);

  // Probe once: are we in a native runtime with the scanner module available?
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core" as any);
        if (!Capacitor?.isNativePlatform?.()) {
          if (!cancelled) setSupported(false);
          return;
        }
        const { BarcodeScanner } = await import("@capacitor-mlkit/barcode-scanning" as any);
        const { supported: ok } = await BarcodeScanner.isSupported();
        if (!cancelled) setSupported(!!ok);
      } catch {
        if (!cancelled) setSupported(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const startScan = useCallback(async () => {
    setState("checking");
    try {
      const { BarcodeScanner } = await import("@capacitor-mlkit/barcode-scanning" as any);

      // Native camera permission. ML Kit uses AVCaptureDevice under the hood,
      // so this triggers the iOS system prompt backed by NSCameraUsageDescription.
      const perm = await BarcodeScanner.requestPermissions();
      const granted = perm?.camera === "granted" || perm?.camera === "limited";
      if (!granted) {
        toast({
          title: "Camera access needed",
          description:
            "Enable camera access for Nexus Dex in Settings to scan a friend's QR code.",
          variant: "destructive",
        });
        setState("idle");
        return;
      }

      setState("scanning");
      // Full-screen native scanner, QR only. Resolves when a code is read or
      // the user cancels (empty barcodes array).
      const result = await BarcodeScanner.scan({ formats: ["QR_CODE"] });
      const value: string | undefined = result?.barcodes?.[0]?.rawValue;
      setState("idle");

      if (!value) return; // user cancelled

      const outcome = await redeemFriendUrl(value, {
        isSignedIn: !!account,
        toast,
        navigate: setLocation,
        onFriendLimit: (message) => openPaywall("qr-scan", message),
      });
      if (outcome.status === "invalid") {
        toast({
          title: "That's not a Nexus Dex code",
          description: "Ask your friend to open Friends → Show my QR and try again.",
          variant: "destructive",
        });
      }
    } catch (err) {
      setState("idle");
      toast({
        title: "Scanner unavailable",
        description: err instanceof Error ? err.message : "Couldn't start the camera.",
        variant: "destructive",
      });
    }
  }, [account, setLocation, toast]);

  const busy = state === "checking" || state === "scanning";

  return (
    <div>
      <div
        className="mx-auto w-full aspect-square max-w-xs rounded-2xl relative overflow-hidden flex items-center justify-center"
        style={{ background: "var(--surface-elev-2)", border: "1px dashed var(--surface-elev-border)" }}
      >
        {/* corner brackets — kept as the framing affordance */}
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="absolute w-8 h-8"
            style={{
              top: i < 2 ? 14 : "auto",
              bottom: i >= 2 ? 14 : "auto",
              left: i % 2 === 0 ? 14 : "auto",
              right: i % 2 === 1 ? 14 : "auto",
              borderTop: i < 2 ? "2px solid hsl(var(--primary))" : "none",
              borderBottom: i >= 2 ? "2px solid hsl(var(--primary))" : "none",
              borderLeft: i % 2 === 0 ? "2px solid hsl(var(--primary))" : "none",
              borderRight: i % 2 === 1 ? "2px solid hsl(var(--primary))" : "none",
              borderRadius: 6,
            }}
          />
        ))}
        <div className="text-center px-6">
          {busy ? (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[hsl(var(--primary))] animate-spin" />
              <p className="mt-2 text-xs text-muted-foreground">
                {state === "checking" ? "Requesting camera…" : "Point at their QR code"}
              </p>
            </>
          ) : supported === false ? (
            <>
              <AlertTriangle className="w-9 h-9 mx-auto text-muted-foreground" />
              <p className="mt-2 text-xs text-muted-foreground">
                Live scanning needs the Nexus Dex app. Use the Code or Name tab instead.
              </p>
            </>
          ) : (
            <>
              <ScanLine className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="mt-2 text-xs text-muted-foreground">Point your camera at their QR code</p>
            </>
          )}
        </div>
        {/* sweep beam only while actively scanning */}
        {state === "scanning" && (
          <div
            className="absolute inset-x-6 h-0.5 scan-beam animate-scan"
            style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary)), transparent)", top: "50%" }}
          />
        )}
      </div>

      {supported !== false && (
        <button
          onClick={startScan}
          disabled={busy || supported === null}
          className="mt-4 w-full rounded-full py-2.5 text-xs font-semibold hover-elevate inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
          style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
          data-testid="button-start-qr-scan"
        >
          <Camera className="w-3.5 h-3.5" /> {busy ? "Scanning…" : "Scan a friend's QR"}
        </button>
      )}

      <button
        onClick={onShowQr}
        className="mt-2 w-full rounded-full py-2.5 text-xs font-semibold hover-elevate inline-flex items-center justify-center gap-1.5"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
        data-testid="button-addfriend-showmine"
      >
        <QrCode className="w-3.5 h-3.5" /> Show my QR instead
      </button>
    </div>
  );
}
